import { useState, useEffect, useCallback } from "react";
import useIsMobile from "states/deviceState";
import SearchHeaderInput from "../SearchList/HeaderInput";
import useDebounce from "utils/useDebounce";
import {
  useSearchDefault,
  useSearchListVerLight,
} from "utils/clientAPIs/searchList";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import ListWithInterest from "../components/ListWithInterest";
import EmptySearchItem from "../components/EmptySearchItem";
import Loading from "components/organisms/m/Loading";
import { TYPO } from "styles/Typo";
import Tabs from "components/common/Tab/tabs";
import Tab from "components/common/Tab/tab";
import { useRecoilState } from "recoil";
import { searchModalState } from "states/SearchModalState";
import {
  useMarketTabTracking,
  useTrackedSearchResults,
} from "lib/analytics/react/hooks";

function SearchModal({ setSearchModal }: { setSearchModal: any }) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const [searchModal] = useRecoilState(searchModalState);
  const { trackClick } = useMarketTabTracking("kr");
  const [search, setSearch] = useState(
    router?.query?.query
      ? (router.query.query as string)
      : searchModal?.search
      ? searchModal?.search
      : ""
  );
  const { t } = useTranslation("search");

  const [debouncedValue, searchTranslated] = useDebounce(search?.trim(), 100);

  const [defaultData, defaultIsValid] = useSearchDefault();

  const [data, isValid] = useSearchListVerLight({
    search: searchTranslated[0],
    search2: searchTranslated[1],
    searchOrigin: debouncedValue,
  });

  // 🔍 검색 추적 (자동으로 처리됨)
  const { trackIfReady } = useTrackedSearchResults(
    debouncedValue,
    router.pathname.startsWith("/detail") ? "stock" : "unified"
  );

  useEffect(() => {
    trackIfReady(data, isValid);
  }, [data, isValid, trackIfReady]);

  const onSearch = useCallback((query: string) => {
    setSearch(query);
  }, []);

  const [type, setType] = useState(
    router.locale === "en" ? "United States" : "Korea (South)"
  );
  const [typeData, setTypeData] = useState<any[]>([]);

  useEffect(() => {
    window?.scrollTo(0, 0);
    if (!defaultIsValid && defaultData?.length > 0) {
      let sorted = [...(defaultData || [])];
      if (type === "Korea (South)") {
        sorted = sorted.filter((item) => item.ITEM_CD.toString()[0] == 5);
      } else if (type === "United States") {
        sorted = sorted.filter((item) => item.ITEM_CD.toString()[0] == 2);
      } else if (type === "Crypto") {
        sorted = sorted.filter((item) => item.ITEM_CD.toString()[0] == 3);
      }
      sorted.sort((a, b) => a.RANK - b.RANK);
      setTypeData(sorted);
    }
  }, [type, defaultIsValid]);

  useEffect(() => {
    return () => {
      if (!router.pathname.startsWith("/detail")) {
        setSearchModal((prev: any) => ({
          ...prev,
          search: "",
        }));
      }
    };
  }, []);

  const currentDate = new Date();
  const year = currentDate.getFullYear() % 100; // 2024년의 경우 24로 변환
  const month = currentDate.getMonth() + 1; // 월은 0부터 시작하므로 1을 더해줌
  const day = currentDate.getDate();

  const renderData = debouncedValue?.length > 0 ? data : typeData;

  return (
    <div
      className={`bg-white absolute top-0  pb-[50px] z-[30] flex flex-col  items-center ${
        isMobile
          ? "top-0 left-0 w-screen "
          : "top-[60px] left-[50%] transform -translate-x-1/2 w-[800px]"
      }
      min-h-screen
      `}
    >
      {isMobile && (
        <div className="w-full">
          <SearchHeaderInput
            setSearchModal={setSearchModal}
            search={search}
            onSearch={onSearch}
            t={t}
            backHandler={() => {
              if (router.pathname.startsWith("/detail")) {
                setSearchModal(false);
                return;
              }
              setSearchModal((prev: any) => ({
                ...prev,
                modalOpen: false,
              }));
            }}
          />
          {debouncedValue.length === 0 && (
            <>
              <Tabs type="line" isFull={true}>
                <Tab
                  title={router.locale === "ko" ? "국내주식" : "Korean"}
                  onClick={() => {
                    trackClick("kr");
                    setType("Korea (South)");
                  }}
                  isActive={type === "Korea (South)"}
                  type={"line"}
                />
                <Tab
                  title={router.locale === "ko" ? "해외주식" : "Global"}
                  onClick={() => {
                    trackClick("us");
                    setType("United States");
                  }}
                  isActive={type === "United States"}
                  type={"line"}
                />
                <Tab
                  title={router.locale === "ko" ? "가상자산" : "Crypto"}
                  onClick={() => {
                    trackClick("crypto");
                    setType("Crypto");
                  }}
                  isActive={type === "Crypto"}
                  type={"line"}
                />
              </Tabs>
              <div className="px-6 py-4 w-full flex-col flex">
                <span className={`${TYPO.TYPO7_MD} !text-[14px] text-gray50`}>
                  {router.locale === "ko"
                    ? `${year}년 ${month}월 ${day}일`
                    : `${year}.${month}.${day}`}
                </span>
                <span className={`${TYPO.TYPO6_SB} text-gray80`}>
                  {router.locale === "ko"
                    ? "사람들이 많이 보고 있어요"
                    : "Trending"}
                </span>
              </div>
            </>
          )}

          {/**검색 리스트 - default 사람들이 가장 많이 보는 자산 */}
          <div>
            {typeData && !isValid && !defaultIsValid && (
              <ListWithInterest
                search={debouncedValue}
                data={renderData}
                count={debouncedValue && debouncedValue?.length > 0 ? 100 : 10}
                type={type}
                isSearching={
                  (debouncedValue && debouncedValue?.length > 0) || false
                }
                listClickHandler={() => {
                  if (router.pathname === "/detail/[ticker]") {
                    setSearchModal(false);
                    return;
                  }
                  setTimeout(() => {
                    setSearchModal((prev: any) => ({
                      ...prev,
                      modalOpen: false,
                    }));
                  }, 300);
                }}
              />
            )}
            {router.pathname === "/detail/[ticker]" &&
              renderData?.length === 0 &&
              !isValid &&
              !defaultIsValid && <EmptySearchItem />}
            {(isValid || defaultIsValid) && <Loading />}
          </div>

          {/* <SearchTable
            search={debouncedValue}
            t={t}
            data={renderData}
            isValid={isValid || defaultIsValid}
            listClickHandler={() => {
              setTimeout(() => {
                setSearchModal((prev: any) => ({
                  ...prev,
                  modalOpen: false,
                }));
              }, 300);
            }}
          /> */}
        </div>
      )}
      {!isMobile && (
        <main className=" slim-scroll  bg-white w-screen  max-w-[800px] min-h-screen pt-3">
          <div className="flex flex-col  gap-1">
            <SearchHeaderInput
              setSearchModal={setSearchModal}
              search={search}
              onSearch={onSearch}
              t={t}
              backHandler={() => {
                if (router.pathname.startsWith("/detail")) {
                  setSearchModal(false);
                  return;
                }
                setSearchModal((prev: any) => ({
                  ...prev,
                  modalOpen: false,
                }));
              }}
            />

            {debouncedValue.length === 0 && (
              <>
                <Tabs type="line" isFull={true}>
                  <Tab
                    title={router.locale === "ko" ? "국내주식" : "Korean"}
                    onClick={() => {
                      trackClick("kr");
                      setType("Korea (South)");
                    }}
                    isActive={type === "Korea (South)"}
                    type={"line"}
                  />
                  <Tab
                    title={router.locale === "ko" ? "해외주식" : "Global"}
                    onClick={() => {
                      trackClick("us");
                      setType("United States");
                    }}
                    isActive={type === "United States"}
                    type={"line"}
                  />
                  <Tab
                    title={router.locale === "ko" ? "가상자산" : "Crypto"}
                    onClick={() => {
                      trackClick("crypto");
                      setType("Crypto");
                    }}
                    isActive={type === "Crypto"}
                    type={"line"}
                  />
                </Tabs>
                <div className="px-6 py-4 w-full flex-col flex">
                  <span className={`${TYPO.TYPO7_MD} !text-[14px] text-gray50`}>
                    {router.locale === "ko"
                      ? `${year}년 ${month}월 ${day}일`
                      : `${year}.${month}.${day}`}
                  </span>
                  <span className={`${TYPO.TYPO6_SB} text-gray80`}>
                    {router.locale === "ko"
                      ? "사람들이 많이 보고 있어요"
                      : "Trending"}
                  </span>
                </div>
              </>
            )}

            {/**검색 리스트 - default 사람들이 가장 많이 보는 자산 */}
            <div>
              {typeData && !isValid && !defaultIsValid && (
                <ListWithInterest
                  search={search}
                  data={renderData}
                  count={
                    debouncedValue && debouncedValue?.length > 0 ? 100 : 10
                  }
                  type={type}
                  isSearching={
                    (debouncedValue && debouncedValue?.length > 0) || false
                  }
                  listClickHandler={() => {
                    if (router.pathname === "/detail/[ticker]") {
                      setSearchModal(false);
                      return;
                    }
                    setTimeout(() => {
                      setSearchModal((prev: any) => ({
                        ...prev,
                        modalOpen: false,
                      }));
                    }, 300);
                  }}
                />
              )}
              {router.pathname === "/detail/[ticker]" &&
                renderData?.length === 0 &&
                !isValid &&
                !defaultIsValid && <EmptySearchItem />}
              {(isValid || defaultIsValid) && <Loading />}
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

export default SearchModal;
